import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import MobileMenu from "./MobileMenu";
import DynamicLink from "../../shared/DynamicLink";
import { MenuItemFieldsFragment } from "@src/generated/graphql";
import { FaArrowRightLong } from "react-icons/fa6";
import { highlightBracketedText } from "src/utils/utils";

type Logo = {
  websiteUrl: string;
  logoImage: {
    url: string;
    description: string;
    title: string;
    link: string;
  };
  logoName: string;
};

type NavigationLink = {
  linkText: string;
  linkAltText: string;
  linkUrl: string;
};

type CallToAction = {
  buttonText: string;
  buttonLink: string;
};

type HeaderProps = {
  logo: Logo[];
  callToAction?: CallToAction[];
  style?: React.CSSProperties;
  saleClassName?: string;
  headerStyle?: React.CSSProperties;
  hamburgerStyle?: React.CSSProperties;
  isHome?: boolean;
  menuItems?: MenuItemFieldsFragment[];
  subMenuItems?: any;
};

const Header = ({
  logo,
  callToAction,
  style,
  saleClassName = "",
  headerStyle,
  isHome,
  hamburgerStyle,
  menuItems,
  subMenuItems,
}: HeaderProps) => {
  const specialPaths = [
    "summit-hero-digital",
    "summit-slalom",
    "summit-dentsu",
    "summit-partner-day",
  ];

  const location = useLocation();
  const pathnameParts = location.pathname.split("/");

  const shouldUseLogo1 = pathnameParts.some((part) =>
    specialPaths.includes(part)
  );

  const currentLogo = shouldUseLogo1 ? logo[1] : logo[0];

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <header className="header-wrapper">
      <div className="container-fluid-header">
        <div className="main-nav-header">
          {logo && (
            <div className="logo">
              <Link to={currentLogo.websiteUrl}>
                <img
                  src={currentLogo.logoImage.url}
                  alt={currentLogo.logoImage.title}
                />
              </Link>
            </div>
          )}
          {/* Desktop Navigation */}
          <nav className="desktop-nav d-md-flex align-items-center">
            <ul className="dropdown">
              {menuItems &&
                menuItems?.length > 0 &&
                menuItems
                  .filter(
                    (menuItem) =>
                      menuItem.orderposition !== null &&
                      menuItem.parentItem === null // Only show items without a parent
                  )
                  .sort(
                    (a, b) => (a.orderposition || 0) - (b.orderposition || 0)
                  )
                  .map((menuItem) => {
                    // Find child items from the same menuItems array
                    const childMenuItems =
                      subMenuItems
                        ?.filter(
                          (item) => item.parentItem?.sys?.id === menuItem.sys.id
                        )
                        ?.sort(
                          (a, b) =>
                            (a.orderposition || 0) - (b.orderposition || 0)
                        ) || [];
                    const hasChildren = childMenuItems?.length > 0;
                    return (
                      <li key={menuItem.sys.id}>
                        <DynamicLink
                          url={menuItem?.linkText?.linkUrl ?? menuItem?.slug}
                          style={style}
                          className={`dropdown-toggle`}
                        >
                          {menuItem?.title}

                          {hasChildren && (
                            <span style={{ marginLeft: "5px" }}>
                              <svg
                                className="dropdown-arrow"
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M2 4L6 8L10 4"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          )}
                        </DynamicLink>
                        {childMenuItems?.length > 0 && (
                          <div className="nav-submenu">
                            <div className="nav-submenu__container">
                              <div className="nav-submenu__list">
                                {childMenuItems.map((childItem) => {
                                  return (
                                    (
                                      <DynamicLink
                                        key={childItem?.sys?.id}
                                        url={childItem?.slug!=="sitecore" ? `/products/${childItem?.slug}` : `/partnership/${childItem?.slug}`}
                                        style={style}
                                        className="nav-submenu__item"
                                      >
                                        <div className="nav-submenu__content">
                                          <span
                                            className="nav-submenu__title"
                                            dangerouslySetInnerHTML={{
                                              __html: highlightBracketedText(
                                                childItem?.title ?? "",
                                                "font-medium"
                                              ),
                                            }}
                                          />
                                          <p className="nav-submenu__description">
                                            {childItem?.description}
                                          </p>
                                          <span className="nav-submenu__link font-medium">
                                            {childItem?.linkText?.linkText}
                                            <svg
                                              width="16"
                                              height="16"
                                              viewBox="0 0 16 16"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M6 12L10 8L6 4"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </span>
                                        </div>
                                      </DynamicLink>
                                    )
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
            </ul>

            <div className="header-right">
              {callToAction?.[0]?.buttonLink && (
                <DynamicLink
                  url={callToAction?.[0]?.buttonLink} // Dynamically generate the route
                  style={style}
                  className="links"
                >
                  <>
                    <span>{callToAction?.[0].buttonText}</span>
                    <svg width="20" height="20" viewBox="0 0 18 18" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.75 9H14.25" stroke="white" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M9.75 13.5L14.25 9" stroke="white" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M9.75 4.5L14.25 9" stroke="white" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>

                  </>
                </DynamicLink>
              )}
              {callToAction?.[1]?.buttonLink && (
                <DynamicLink
                  url={callToAction?.[1]?.buttonLink}
                  className="btn-white hs-cta-embed hs-cta-simple-placeholder hs-cta-embed-191532026797"
                  data-hubspot-wrapper-cta-id="191532026797"
                >
                  <span>{callToAction?.[1].buttonText}</span>
                  <FaArrowRightLong className="btn-icon" />
                </DynamicLink>
              )}
            </div>
          </nav>

          {/* Mobile Hamburger */}
          <div
            className={`hamburger  ${hamburgerStyle ? "hamburger-bg" : ""}`}
            onClick={toggleMobileMenu}
          >
            <span className="line" style={hamburgerStyle}></span>
            <span className="line" style={hamburgerStyle}></span>
            <span className="line" style={hamburgerStyle}></span>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        logo={logo}
        callToAction={callToAction}
        style={style}
        menuItems={menuItems}
        subMenuItems={subMenuItems}
        onClose={toggleMobileMenu}
      />
    </header>
  );
};

export default Header;
